import { Controller, useFormContext, useWatch } from "react-hook-form";
import { ArrowRight, ArrowRightLeft, Route } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OptionTiles } from "@/components/ui/option-tiles";
import { Field } from "@/components/ui/field";
import { WizardCard } from "../WizardCard";
import type { TripFormValues } from "../schema";
import type { WizardStepStatus } from "../WizardCard";

interface TripTypeStepProps {
  index: number;
  status: WizardStepStatus;
  onContinue: () => void;
  onEdit: () => void;
}

const LABEL: Record<string, string> = {
  one_way: "One Way",
  round_trip: "Round Trip",
  multi_leg: "Multi-Leg",
};

export function TripTypeStep({
  index,
  status,
  onContinue,
  onEdit,
}: TripTypeStepProps) {
  const { control, formState: { errors } } = useFormContext<TripFormValues>();
  const value = useWatch({ control, name: "tripType" }) ?? "";

  if (status === "complete") {
    return (
      <WizardCard
        index={index}
        title="Trip type"
        status="complete"
        summary={LABEL[value] ?? "—"}
        onEdit={onEdit}
      />
    );
  }

  return (
    <WizardCard
      index={index}
      title="Trip type"
      description="Pick how the trip is structured."
      status={status}
    >
      <Controller
        control={control}
        name="tripType"
        render={({ field }) => (
          <Field label="" error={errors.tripType?.message}>
            <OptionTiles
              ariaLabel="Trip type"
              className="sm:grid-cols-3"
              value={field.value ?? ""}
              onValueChange={(v) => {
                const wasEmpty = !field.value;
                field.onChange(v);
                // Auto-advance only on the first-ever selection. Returning
                // users (via Edit) have to confirm via the Continue button
                // so they don't get bounced past a step they only opened
                // to look at.
                if (wasEmpty) onContinue();
              }}
              options={[
                {
                  value: "one_way",
                  label: "One Way",
                  description: "A single ride from pick-up to drop-off.",
                  icon: <ArrowRight className="h-[18px] w-[18px]" />,
                },
                {
                  value: "round_trip",
                  label: "Round Trip",
                  description: "Out and back, with a return pick-up.",
                  icon: <ArrowRightLeft className="h-[18px] w-[18px]" />,
                },
                {
                  value: "multi_leg",
                  label: "Multi-Leg",
                  description: "Several stops in one booking.",
                  icon: <Route className="h-[18px] w-[18px]" />,
                },
              ]}
              invalid={Boolean(errors.tripType)}
            />
          </Field>
        )}
      />
      <div className="flex justify-end">
        <Button
          type="button"
          onClick={onContinue}
          disabled={!value}
          className="min-w-[10rem]"
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </WizardCard>
  );
}
